import { Plus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { AdminUserDetailModal } from '../components/AdminUserDetailModal';
import { AdminUsersTable } from '../components/AdminUsersTable';
import { CreateAdminUserModal } from '../components/CreateAdminUserModal';
import { useAdminUsers } from '../hooks/useAdminUsers';
import {
  ADMIN_USER_STATUS_LABELS,
  type AdminUserListDto,
  type AdminUserStatus,
} from '../types/admin-user.types';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ahkinia/Select/select';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

const STATUS_OPTIONS: { value: AdminUserStatus; label: string }[] = [
  { value: 'Active', label: ADMIN_USER_STATUS_LABELS.Active },
  { value: 'Inactive', label: ADMIN_USER_STATUS_LABELS.Inactive },
  { value: 'PendingActivation', label: ADMIN_USER_STATUS_LABELS.PendingActivation },
];

export default function AdminUsersListPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState<AdminUserStatus | ''>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<AdminUserListDto | null>(null);

  const { data, isLoading, isError } = useAdminUsers({
    page,
    pageSize,
    status: status || undefined,
    searchTerm: searchTerm || undefined,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatus('');
    setPage(1);
  };

  const hasFilters = Boolean(searchTerm || status);

  return (
    <div className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Users />}
        title="Usuarios"
        subtitle="Gestiona los usuarios del sistema"
      >
        <Protected permissions={PERMISSIONS.USERS_CREATE}>
          <UnifiedButton
            buttonType="basic"
            icon={<Plus />}
            variant="outline"
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="admin-users-add-button"
          >
            Agregar usuario
          </UnifiedButton>
        </Protected>
      </SectionHeader>

      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="admin-users-search-input" className="text-xs text-primary">
            Buscar
          </label>
          <Input
            id="admin-users-search-input"
            data-testid="admin-users-search-input"
            placeholder="Nombre o correo..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-60"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="admin-users-status-filter" className="text-xs text-primary">
            Estado
          </label>
          <Select
            value={status}
            onValueChange={(val) => {
              setStatus(val as AdminUserStatus | '');
              setPage(1);
            }}
          >
            <SelectTrigger
              id="admin-users-status-filter"
              className="w-52"
              data-testid="admin-users-status-filter"
            >
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`admin-users-status-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasFilters && (
          <UnifiedButton
            data-testid="admin-users-clear-filters-button"
            variant="outline"
            onClick={handleClearFilters}
          >
            <X size={16} />
            Limpiar filtros
          </UnifiedButton>
        )}
      </div>

      {isLoading && <div className="h-full" data-testid="admin-users-loading" aria-hidden />}

      {!isLoading && (isError || !data) && (
        <div className="flex items-center justify-center py-12" data-testid="admin-users-error">
          <p className="text-negative">Error al cargar los usuarios administrativos.</p>
        </div>
      )}

      {!isLoading && !isError && data && (
        <AdminUsersTable
          data={data.items}
          pagination={{
            page: data.page,
            pageSize: data.pageSize,
            totalCount: data.totalCount,
            totalPages: data.totalPages,
            hasPreviousPage: data.hasPreviousPage,
            hasNextPage: data.hasNextPage,
            firstItemOnPage: data.firstItemOnPage,
            lastItemOnPage: data.lastItemOnPage,
          }}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onView={setViewingUser}
        />
      )}

      <AdminUserDetailModal user={viewingUser} onClose={() => setViewingUser(null)} />

      <CreateAdminUserModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
    </div>
  );
}
